import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Button } from 'react-bootstrap';
import { BsArrowCounterclockwise } from 'react-icons/bs';

const customStyles = {
    control: (base) => ({
        ...base,
        minHeight: '38px',
        fontSize: '0.875rem',
        borderColor: '#ced4da'
    }),
    option: (base) => ({
        ...base,
        fontSize: '0.875rem'
    }),
    multiValue: (base) => ({
        ...base,
        fontSize: '0.8rem'
    }),
    valueContainer: (base) => ({
        ...base,
        maxHeight: '70px',
        overflowY: 'auto'
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999
    })
};

const weekEndingStyles = {
    ...customStyles,
    control: (base) => ({
        ...customStyles.control(base),
        minWidth: '120px',
        maxWidth: '140px'
    })
};

const periodStyles = {
    ...customStyles,
    control: (base) => ({
        ...customStyles.control(base),
        minWidth: '100px',
        maxWidth: '120px'
    })
};

const roleStyles = {
    ...customStyles,
    control: (base) => ({
        ...customStyles.control(base),
        maxWidth: '150px'
    })
};

const CostingSummaryFilters = ({
    rawData,
    filters,
    setFilters,
    summarySelection: leftSelection,
    detailSelection: rightSelection,
    onClearAll,
    tourButton
}) => {
    const filterOptions = useMemo(() => {
        if (!rawData || rawData.length === 0) {
            return { sites: [], providers: [], roles: [], weeks: [], periods: [] };
        }

        const uniqueSites = [...new Set(rawData.map(r => r.site_Name).filter(Boolean))].sort();
        const uniqueProviders = [...new Set(rawData.map(r => r.providerName).filter(Boolean))].sort();
        const uniqueRoles = [...new Set(rawData.map(r => r.roleName).filter(Boolean))].sort();

        const uniqueWeeks = [...new Set(rawData.map(r => {
            if (r.weekEnding) {
                const date = new Date(r.weekEnding);
                return date.toISOString().split('T')[0];
            }
            return null;
        }).filter(Boolean))].sort().reverse();

        const periodMap = new Map();
        rawData.forEach(r => {
            if (r.periodName) {
                const key = `${r.periodYr}-${r.periodNum}`;
                if (!periodMap.has(key)) {
                    periodMap.set(key, { name: r.periodName, yr: r.periodYr, num: r.periodNum });
                }
            }
        });
        const uniquePeriods = Array.from(periodMap.values())
            .sort((a, b) => {
                if (b.yr !== a.yr) return b.yr - a.yr;
                return b.num - a.num;
            })
            .map(p => p.name);

        return {
            sites: uniqueSites.map(s => ({ value: s, label: s })),
            providers: uniqueProviders.map(p => ({ value: p, label: p })),
            roles: uniqueRoles.map(r => ({ value: r, label: r })),
            weeks: uniqueWeeks.map(w => ({ value: w, label: w })),
            periods: uniquePeriods.map(p => ({ value: p, label: p }))
        };
    }, [rawData]);

    const handleFilterChange = useCallback((filterName, selectedOptions) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: selectedOptions || []
        }));
    }, [setFilters]);

    const hasActiveFilters = useMemo(() => {
        return (
            filters.sites?.length > 0 ||
            filters.providers?.length > 0 ||
            filters.roles?.length > 0 ||
            filters.weeks?.length > 0 ||
            filters.periods?.length > 0 ||
            leftSelection !== null ||
            rightSelection !== null
        );
    }, [filters, leftSelection, rightSelection]);

    return (
        <div className="filters-section">
            <div className="filter-row">
                <div className="filter-item">
                    <label>Site</label>
                    <Select
                        isMulti
                        isClearable
                        isSearchable
                        placeholder="All Sites"
                        options={filterOptions.sites}
                        value={filters.sites}
                        onChange={(val) => handleFilterChange('sites', val)}
                        classNamePrefix="react-select"
                        styles={customStyles}
                    />
                </div>

                <div className="filter-item">
                    <label>Provider</label>
                    <Select
                        isMulti
                        isClearable
                        isSearchable
                        placeholder="All Providers"
                        options={filterOptions.providers}
                        value={filters.providers}
                        onChange={(val) => handleFilterChange('providers', val)}
                        classNamePrefix="react-select"
                        styles={customStyles}
                    />
                </div>

                <div className="filter-item filter-item-narrow">
                    <label>Role</label>
                    <Select
                        isMulti
                        isClearable
                        isSearchable
                        placeholder="All Roles"
                        options={filterOptions.roles}
                        value={filters.roles}
                        onChange={(val) => handleFilterChange('roles', val)}
                        classNamePrefix="react-select"
                        styles={roleStyles}
                    />
                </div>

                <div className="filter-item filter-item-narrow">
                    <label>Week Ending</label>
                    <Select
                        isMulti
                        isClearable
                        isSearchable
                        placeholder="All Weeks"
                        options={filterOptions.weeks}
                        value={filters.weeks}
                        onChange={(val) => handleFilterChange('weeks', val)}
                        classNamePrefix="react-select"
                        styles={weekEndingStyles}
                    />
                </div>

                <div className="filter-item filter-item-narrow">
                    <label>Period</label>
                    <Select
                        isMulti
                        isClearable
                        isSearchable
                        placeholder="All Periods"
                        options={filterOptions.periods}
                        value={filters.periods}
                        onChange={(val) => handleFilterChange('periods', val)}
                        classNamePrefix="react-select"
                        styles={periodStyles}
                    />
                </div>

                {hasActiveFilters && (
                    <Button
                        variant="outline-secondary"
                        className="clear-filters-btn"
                        onClick={onClearAll}
                        title="Clear All Filters"
                    >
                        <BsArrowCounterclockwise className="me-1" />
                        Clear All
                    </Button>
                )}

                {tourButton}
            </div>
        </div>
    );
};

CostingSummaryFilters.propTypes = {
    rawData: PropTypes.array,
    filters: PropTypes.shape({
        sites: PropTypes.array,
        providers: PropTypes.array,
        roles: PropTypes.array,
        weeks: PropTypes.array,
        periods: PropTypes.array,
    }).isRequired,
    setFilters: PropTypes.func.isRequired,
    summarySelection: PropTypes.object,
    detailSelection: PropTypes.object,
    onClearAll: PropTypes.func.isRequired,
    tourButton: PropTypes.node
};

export default CostingSummaryFilters;
